/**
 * Feature E08-F01 — Outlook OAuth connect/disconnect/status — typed api-client
 * calls + TanStack Query hooks (web side, E08-F01-T06).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). Transport uses the
 * shared `apiClient` (auto-injects the session Bearer token, normalises the
 * response, never throws) + `unwrap`. Contracts come from `@eer/shared`
 * (outlook-oauth.ts) so the SPA speaks the exact shapes the Fastify route
 * validates.
 *
 * Endpoints (routes/outlook-oauth.ts — top-level, no prefix):
 *   GET  /outlook/authorize-url   → useAuthorizeUrl()    (build the popup URL)
 *   GET  /outlook/status          → useOutlookStatus()   (session user's status)
 *   POST /outlook/disconnect      → useDisconnectOutlook() (own id or, admin, another)
 *
 * The OAuth round-trip is a popup: the route returns an HTML page that
 * `postMessage`s `{ type:'oauth-success' | 'oauth-error', ... }` to the opener —
 * the component (`OutlookConnect`) opens the popup + listens (this module owns
 * only the typed fetches, keeping the side-effectful popup wiring at the UI layer).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  authorizeUrlResponseSchema,
  outlookStatusResponseSchema,
  disconnectOutlookResponseSchema,
  type AuthorizeUrlResponse,
  type OutlookStatusResponse,
  type DisconnectOutlookResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { USERS_QUERY_KEY } from './usersApi.js';

/** Feature-local query keys (kept out of the shared registry). */
export const OUTLOOK_STATUS_QUERY_KEY = ['outlook', 'status'] as const;

/** GET /outlook/authorize-url — the fully-built Microsoft authorize URL. */
export async function fetchAuthorizeUrl(): Promise<AuthorizeUrlResponse> {
  const result = await apiClient.get<unknown>('/outlook/authorize-url', {
    command: 'get_outlook_authorize_url',
  });
  return authorizeUrlResponseSchema.parse(unwrap(result));
}

/** GET /outlook/status — the session user's live connection status. */
export async function fetchOutlookStatus(): Promise<OutlookStatusResponse> {
  const result = await apiClient.get<unknown>('/outlook/status', {
    command: 'get_outlook_status',
  });
  return outlookStatusResponseSchema.parse(unwrap(result));
}

/** POST /outlook/disconnect — own id (omit) or, for an admin, another user's id. */
export async function postDisconnectOutlook(userId?: string): Promise<DisconnectOutlookResponse> {
  const result = await apiClient.post<unknown>('/outlook/disconnect', userId ? { userId } : {}, {
    command: 'disconnect_outlook',
  });
  return disconnectOutlookResponseSchema.parse(unwrap(result));
}

/** The session user's Outlook status (sidebar widget). */
export function useOutlookStatus(
  enabled: boolean = true,
): UseQueryResult<OutlookStatusResponse, ApiResultError> {
  return useQuery<OutlookStatusResponse, ApiResultError>({
    queryKey: OUTLOOK_STATUS_QUERY_KEY,
    queryFn: fetchOutlookStatus,
    enabled,
  });
}

/**
 * Disconnect mutation. Invalidates BOTH the session status and the admin Users
 * list (the admin badge reads has_outlook from get_users), so either surface
 * updates after a disconnect.
 */
export function useDisconnectOutlook(): UseMutationResult<
  DisconnectOutlookResponse,
  ApiResultError,
  string | undefined
> {
  const queryClient = useQueryClient();
  return useMutation<DisconnectOutlookResponse, ApiResultError, string | undefined>({
    mutationFn: (userId) => postDisconnectOutlook(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OUTLOOK_STATUS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}
